import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRegionManagement } from '../hooks/useRegionManagement';
import { selectSortedTsps } from '../map/slice/mapSelectors';
import {
  useGetTSPsQuery,
  useGetZonesQuery,
  useGetRegionsQuery,
} from '../services/edgeApi';
import { TSPBadge } from '../helpers/TSPBadge';
import { ZoneBadge } from '../helpers/ZoneBadge';
import {
  CheckCircleIcon,
  CloseCircleIcon,
  CloseIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  UnionIcon,
} from '@iconicicons/react';
import { colors } from '../utils/colors';
import { TSPRegion } from '@/types/api/TSP';
import { ZoneRegion } from '@/types/api/Zone';
import { Region } from '@/types';
import { MultiSelectButton } from '../helpers/MultiSelect';
import Fuse from 'fuse.js';
import { titleize } from '../utils/stringUtils';
import { useDebouncedState } from '../helpers/hooks';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import toast from 'react-hot-toast';

const HEADER_HEIGHT = 42;
const STICKY_OFFSET = HEADER_HEIGHT + 8;
const STATUSES = ['complete', 'partial', 'unassigned'];

type RegionPillProps = {
  region: TSPRegion | ZoneRegion;
  onSplit?: (region: TSPRegion | ZoneRegion) => void;
  onRemove?: (region: TSPRegion | ZoneRegion) => void;
  disabled?: boolean;
};

const RegionPill = ({
  region,
  onSplit,
  onRemove,
  disabled,
}: RegionPillProps) => {
  return (
    <span
      key={region.id}
      style={{
        padding: '2px 6px',
        borderRadius: '8px',
        backgroundColor: 'var(--bs-secondary-bg)',
      }}
    >
      {region.name}{' '}
      {onSplit && (
        <span title="Split into zones">
          <UnionIcon
            role="button"
            onClick={() => !disabled && onSplit(region)}
          />
        </span>
      )}
      {onRemove && (
        <span title="Remove from region">
          <CloseIcon
            role="button"
            onClick={() => !disabled && onRemove(region)}
          />
        </span>
      )}
    </span>
  );
};

type AddRegionButtonProps = {
  onAdd: (region: Region) => void;
  regions?: Region[];
  selectorId: string;
  disabled?: boolean;
};

const AddRegionButton = ({
  onAdd,
  regions,
  selectorId,
  disabled,
}: AddRegionButtonProps) => {
  return (
    <div className="dropdown">
      <PlusCircleIcon
        role="button"
        id={selectorId}
        className="text-muted dropdown-toggle"
        data-bs-toggle="dropdown"
      />
      <ul className="dropdown-menu" aria-labelledby={selectorId}>
        {regions?.map((region) => {
          return (
            <li key={region.id}>
              <p
                className="dropdown-item"
                role="button"
                onClick={() => !disabled && onAdd(region)}
              >
                {region.name}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const RegionAssignmentTable = () => {
  const { isLoading: isLoadingTsps, isFetching: isFetchingTsps } =
    useGetTSPsQuery();
  const {
    data: zones,
    isLoading: isLoadingZones,
    isFetching: isFetchingZones,
  } = useGetZonesQuery(undefined);
  const tsps = useSelector(selectSortedTsps);
  const { data: regions } = useGetRegionsQuery(undefined);
  const [searchTerm, setSearchTerm] = useDebouncedState<string>('', 300);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const {
    addPipeToRegion,
    removePipeFromRegion,
    splitTSPInRegion,
    addZoneToRegion,
    removeZoneFromRegion,
    isUpdating,
  } = useRegionManagement();

  const TSPFuse = useMemo(() => {
    return new Fuse(tsps, {
      keys: ['name', 'id', 'short_name', 'long_name', 'aliases'],
      threshold: 0.2,
    });
  }, [tsps]);

  const zoneFuse = useMemo(() => {
    return new Fuse(Object.values(zones ?? {}), {
      keys: ['name', 'id'],
      threshold: 0.2,
    });
  }, [zones]);

  const filteredTsps = useMemo(() => {
    const filteredTspIds = new Set(
      TSPFuse.search(searchTerm).map(({ item: tsp }) => tsp.id),
    );
    const filteredZoneIds = new Set(
      zoneFuse
        .search(searchTerm)
        .filter(({ item: zone }) => Boolean(zone.tsp))
        .map(({ item: zone }) => zone.tsp!.id),
    );

    return tsps
      .filter(
        (tsp) =>
          searchTerm.length === 0 ||
          filteredTspIds.has(tsp.id) ||
          filteredZoneIds.has(tsp.id),
      )
      .filter((tsp) => {
        const tspZones = Object.values(zones ?? {}).filter((zone) => {
          return zone.tsp && zone.tsp.id === tsp.id;
        });
        const complete =
          tsp.regions.length > 0 ||
          tspZones.every((zone) => zone.regions.length > 0);
        const partial =
          !complete && tspZones.some((zone) => zone.regions.length > 0);
        const status = complete
          ? 'complete'
          : partial
          ? 'partial'
          : 'unassigned';
        return (
          selectedStatuses.length === 0 || selectedStatuses.includes(status)
        );
      });
  }, [tsps, zones, searchTerm, TSPFuse, zoneFuse, selectedStatuses]);

  useEffect(() => {
    if (
      (isFetchingZones && !isLoadingZones) ||
      (isFetchingTsps && !isLoadingTsps)
    ) {
      let toastId = toast.loading('Updating regions...');
      return () => {
        toast.success('Regions updated', { id: toastId });
      };
    }
    return () => {};
  }, [isFetchingZones, isFetchingTsps]);

  return (
    <div>
      <div className="m-2 d-flex justify-content-between gap-2">
        <MultiSelectButton
          options={STATUSES}
          isMarked={(status) => selectedStatuses.includes(status)}
          onToggle={(status, checked) => {
            setSelectedStatuses((prev) => {
              if (checked) {
                return [...prev, status];
              } else {
                return prev.filter((s) => s !== status);
              }
            });
          }}
          keyExtractor={(status) => status}
          optionRenderer={(status) => titleize(status)}
          title="Filter by Status"
        />
        <input
          type="text"
          className="form-control"
          placeholder="Search..."
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      {isLoadingTsps || isLoadingZones ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '200px' }}
        >
          <LoadingIndicator />
        </div>
      ) : (
        <table className="table">
          <thead
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              height: `${HEADER_HEIGHT}px`,
            }}
          >
            <tr>
              <th className="text-center">Status</th>
              <th className="text-center">Pipeline</th>
              <th className="text-center">Pipe Regions</th>
              <th className="text-center">Zone</th>
              <th className="text-center">Zone Regions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTsps.map((tsp) => {
              const tspZones = Object.values(zones ?? {})
                .filter((zone) => {
                  return zone.tsp && zone.tsp.id === tsp.id;
                })
                .sort((a, b) => a.name.localeCompare(b.name));
              const complete =
                tsp.regions.length > 0 ||
                tspZones.every((zone) => zone.regions.length > 0);
              const partial =
                !complete && tspZones.some((zone) => zone.regions.length > 0);
              return (
                <React.Fragment key={tsp.id.toString()}>
                  <tr key={tsp.id.toString()} style={{ borderTopWidth: '3px' }}>
                    <td rowSpan={tspZones.length || 1}>
                      <div
                        className="w-100 h-100 d-flex justify-content-center px-3"
                        style={{ position: 'sticky', top: STICKY_OFFSET }}
                      >
                        {complete ? (
                          <span style={{ color: colors.matteGreen }}>
                            Complete
                          </span>
                        ) : partial ? (
                          <span style={{ color: colors.matteYellow }}>
                            Partial
                          </span>
                        ) : (
                          <span style={{ color: colors.matteRed }}>
                            Unassigned
                          </span>
                        )}
                      </div>
                    </td>
                    <td rowSpan={tspZones.length || 1}>
                      <div
                        className="w-100 h-100 d-flex justify-content-center px-3"
                        style={{ position: 'sticky', top: STICKY_OFFSET }}
                      >
                        <TSPBadge
                          tsp={tsp}
                          className="w-100"
                          extraContent={
                            complete ? (
                              <CheckCircleIcon
                                style={{ color: colors.matteGreen }}
                              />
                            ) : partial ? (
                              <MinusCircleIcon
                                style={{ color: colors.matteYellow }}
                              />
                            ) : (
                              <CloseCircleIcon
                                style={{ color: colors.matteRed }}
                              />
                            )
                          }
                        />
                      </div>
                    </td>
                    <td rowSpan={tspZones.length || 1} className="text-center">
                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        {tsp.regions
                          .slice()
                          .sort((a, b) => a.id - b.id)
                          .map((region) => (
                            <RegionPill
                              key={region.id}
                              region={region}
                              onSplit={() => splitTSPInRegion(region, tsp)}
                              onRemove={() => removePipeFromRegion(region, tsp)}
                              disabled={isFetchingTsps || isUpdating}
                            />
                          ))}
                      </div>
                      <div className="text-center my-1">
                        <AddRegionButton
                          regions={regions?.filter(
                            (region) =>
                              !tsp.regions.some((r) => r.id === region.id),
                          )}
                          onAdd={(region) => addPipeToRegion(region, tsp)}
                          selectorId={`add-region-to-tsp-${tsp.id}`}
                          disabled={isFetchingTsps || isUpdating}
                        />
                      </div>
                    </td>
                    <td>
                      <div
                        className="w-100 h-100 d-flex justify-content-center px-3"
                        style={{ position: 'sticky', top: STICKY_OFFSET }}
                      >
                        <ZoneBadge zone={tspZones[0]} className="w-100" />
                      </div>
                    </td>
                    <td key={tspZones[0]?.id} className="text-center">
                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        {tspZones[0]?.regions
                          .slice()
                          .sort((a, b) => a.id - b.id)
                          .map((region) => (
                            <RegionPill
                              key={region.id}
                              region={region}
                              onRemove={() =>
                                removeZoneFromRegion(region, tspZones[0])
                              }
                              disabled={isFetchingZones || isUpdating}
                            />
                          ))}

                        {/* {(
                          tsp?.regions.filter(
                            (region) =>
                              !tspZones[0]?.regions.some(
                                (r) => r.id === region.id,
                              ),
                          ) ?? []
                        ).map((region) => (
                          <RegionPill
                            key={region.id}
                            region={region}
                            disabled
                          />
                        ))} */}
                      </div>
                      <p className="text-center my-1">
                        <AddRegionButton
                          regions={regions?.filter(
                            (region) =>
                              !tspZones[0]?.regions.some(
                                (r) => r.id === region.id,
                              ),
                          )}
                          onAdd={(region) =>
                            addZoneToRegion(region, tspZones[0])
                          }
                          selectorId={`add-region-to-zone-${tspZones[0]?.id}`}
                          disabled={isFetchingZones || isUpdating}
                        />
                      </p>
                    </td>
                  </tr>
                  {tspZones.slice(1, undefined).map((zone) => {
                    // const regionsThroughTSP =
                    //   tsp?.regions.filter(
                    //     (region) => !zone.regions.some((r) => r.id === region.id),
                    //   ) ?? [];
                    return (
                      <tr key={zone.id}>
                        <td>
                          <div
                            className="w-100 h-100 d-flex justify-content-center px-3"
                            style={{
                              position: 'sticky',
                              top: STICKY_OFFSET,
                            }}
                          >
                            <ZoneBadge zone={zone} className="w-100" />
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="d-flex flex-wrap justify-content-center gap-2">
                            {zone.regions
                              .slice()
                              .sort((a, b) => a.id - b.id)
                              .map((region) => (
                                <RegionPill
                                  key={region.id}
                                  region={region}
                                  onRemove={() =>
                                    removeZoneFromRegion(region, zone)
                                  }
                                  disabled={isFetchingZones || isUpdating}
                                />
                              ))}
                            {/* {regionsThroughTSP.map((region) => (
                              <RegionPill
                                key={region.id}
                                region={region}
                                disabled
                              />
                            ))} */}
                          </div>
                          <p className="text-center my-1">
                            <AddRegionButton
                              regions={regions?.filter(
                                (region) =>
                                  !zone.regions.some((r) => r.id === region.id),
                              )}
                              onAdd={(region) => addZoneToRegion(region, zone)}
                              selectorId={`add-region-to-zone-${zone.id}`}
                              disabled={isFetchingZones || isUpdating}
                            />
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
